import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";

export interface ExhibitionStandCategory {
  id: number
  exhibition: number
  category: number
  created_by?: string
  updated_by?: string
    stand_name?: string
}

export interface ExhibitionPricing {
  id: number
  exhibition: number
  badge_fee: number
  badge_reprint_price: number
  visitor_entry_fee: number
  parking_fee_per_minute: number
  currency: string
    created_by?: string
    updated_by?: string
 
}

// Update the types to match what the service actually returns
export interface Participation {
  id: number
  exhibition_title?: string
  exhibitor_name?: string
  stand_category_name?: string
  status: string | boolean
  created_by?: string
  created_at?: string
  // Optional nested objects that might be included
  exhibition?: {
    id: number
    title: string
    [key: string]: any
  }
  exhibitor?: {
    id: number
    name: string
    [key: string]: any
  }
  stand_category?: {
    id: number
    name: string
    [key: string]: any
  }
}


const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  total_pages: number
  current_page: number
  page_size: number
  results: T[]
}
export const AllStandCategoryService = {
  getAll: async (): Promise<ExhibitionStandCategory[]> => {
    const response = await api.get("/api/exhibitions/get-stand-categories/")
    return response.data
  }
}

export const ExhibitionStandCategoryService = {
  getAll: async (exhibitionId: number): Promise<PaginatedResponse<ExhibitionStandCategory>> => {
    const response = await api.get(`/api/exhibitions/exhibition-stand-categories-by-exhibition/${exhibitionId}/`)
    return response.data
  },

  create: async (data: Omit<ExhibitionStandCategory, "id">): Promise<ExhibitionStandCategory> => {
    const response = await api.post(`/api/exhibitions/exhibition-stand-categories/create/`, data)
    return response.data
  },

  update: async (id: number, data: Omit<ExhibitionStandCategory, "id">): Promise<ExhibitionStandCategory> => {
    const response = await api.put(`/api/exhibitions/exhibition-stand-categories/${id}/update/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/exhibitions/exhibition-stand-categories/${id}/delete/`)
  },
}

export const ExhibitionPricingService = {
  get: async (exhibitionId: number): Promise<ExhibitionPricing> => {
    const response = await api.get(`/api/exhibitions/pricing-by-exhibition/${exhibitionId}/`)
    return response.data
  },

  create: async (data: Omit<ExhibitionPricing, "id">): Promise<ExhibitionPricing> => {
    const response = await api.post(`/api/exhibitions/pricing/create/`, data)
    return response.data
  },

  update: async (id: number, data: Omit<ExhibitionPricing, "id">): Promise<ExhibitionPricing> => {
    const response = await api.put(`/api/exhibitions/pricing/${id}/update/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/exhibitions/pricing/${id}/delete/`)
  },
}

export const ParticipationService = {
  getAll: async (exhibitionId: number): Promise<PaginatedResponse<Participation>> => {
    const response = await api.get(`/api/stakeholders/participations-by-exhibition/${exhibitionId}/`)
    return response.data
  },

  create: async (data: Omit<Participation, "id">): Promise<Participation> => {
    const response = await api.post(`/api/stakeholders/participations/create/`, data)
    return response.data
  },

  update: async (id: number, data: Omit<Participation, "id">): Promise<Participation> => {
    const response = await api.put(`/api/stakeholders/participations/${id}/update/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/stakeholders/participations/${id}/delete/`)
  },
}
